import { useFormStore } from "@/stores/use-form-store"
import FieldInfoIcon from "../../components/field-info"
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../../components/ui/form"
import { Textarea } from "../../components/ui/textarea"
import { cn, parseAttributes } from "../../lib/utils"
import {
	TextAreaConfig,
	type Attribute,
	type FieldProps,
} from "../../types/form-types"
import { Description } from "./components/description"
import { useLogicStore } from "@/stores/use-conditional-logic-store"
import { ErrorMessage } from "./components/error-message"

export const TextAreaField = ({
	form,
	field,
	commonAttributes,
}: FieldProps) => {
	const { label, infoIconText, infoIconType, description, infoIconRequired } =
		commonAttributes
	const { placeholder } = getTextAreaInputAttributes(field.attributes)
	const { shouldDisable, verifiedFields } = useFormStore()

	const isVerified = verifiedFields.get(field.field_id) || false
	const { hiddenFields, errors } = useLogicStore()

	if (hiddenFields[field.field_id]) return null

	return (
		<FormField
			name={field.field_id}
			control={form.control}
			render={({ field: formField }) => (
				<FormItem>
					<FormLabel>
						<div className="flex flex-col gap-1">
							<div className="flex items-center gap-1">
								<p
									className={cn(
										field.is_required &&
											"after:ml-1 after:text-red-500 after:content-['*']",
									)}
								>
									{label}
								</p>
								{infoIconRequired && (
									<FieldInfoIcon
										infoText={infoIconText}
										iconType={infoIconType}
									/>
								)}
							</div>
							{description && (
								<Description description={description} />
							)}
						</div>
					</FormLabel>
					<FormControl>
						<Textarea
							disabled={isVerified || shouldDisable}
							placeholder={placeholder}
							className="resize-none"
							{...formField}
						/>
					</FormControl>
					<FormMessage />
					<ErrorMessage error={errors[field.field_id]} />
				</FormItem>
			)}
		/>
	)
}

function getTextAreaInputAttributes(attributes: Attribute[]) {
	const props: {
		placeholder?: string
	} = {}

	const parsedAttributes = parseAttributes(attributes)
	Object.entries(parsedAttributes).forEach(([key, value]) => {
		switch (key) {
			case TextAreaConfig.PlaceholderText:
				props.placeholder = value as string
				break
		}
	})

	return props
}
